import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem("np_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Public ───

export const fetchExperiences = async () => {
  const { data } = await axios.get(`${API}/experiences`);
  return data;
};

export const createBooking = async (bookingData) => {
  const { data } = await axios.post(`${API}/bookings`, bookingData);
  return data;
};

export const verifyPayment = async (paymentData) => {
  const { data } = await axios.post(`${API}/verify-payment`, paymentData);
  return data;
};

// ─── Admin Auth ───

export const adminLogin = async (password) => {
  const { data } = await axios.post(`${API}/admin/login`, { password });
  if (data.token) {
    localStorage.setItem("np_admin_token", data.token);
  }
  return data;
};

export const adminGoogleAuth = async (sessionId) => {
  const { data } = await axios.post(`${API}/admin/google-auth`, { session_id: sessionId });
  if (data.token) {
    localStorage.setItem("np_admin_token", data.token);
  }
  return data;
};

export const adminLogout = async () => {
  try {
    await axios.post(`${API}/admin/logout`, {}, { headers: getAuthHeader() });
  } catch (e) { /* ignore */ }
  localStorage.removeItem("np_admin_token");
  localStorage.removeItem("np_admin_auth");
};

export const changeAdminPassword = async (currentPassword, newPassword) => {
  const { data } = await axios.post(`${API}/admin/change-password`, {
    current_password: currentPassword,
    new_password: newPassword,
  }, { headers: getAuthHeader() });
  return data;
};

// ─── Admin Experiences ───

export const fetchAdminExperiences = async () => {
  const { data } = await axios.get(`${API}/admin/experiences`, { headers: getAuthHeader() });
  return data;
};

export const createExperience = async (expData) => {
  const { data } = await axios.post(`${API}/admin/experiences`, expData, { headers: getAuthHeader() });
  return data;
};

export const updateExperience = async (id, expData) => {
  const { data } = await axios.put(`${API}/admin/experiences/${id}`, expData, { headers: getAuthHeader() });
  return data;
};

export const deleteExperience = async (id) => {
  const { data } = await axios.delete(`${API}/admin/experiences/${id}`, { headers: getAuthHeader() });
  return data;
};

export const reorderExperiences = async (orderedIds) => {
  const { data } = await axios.post(`${API}/admin/experiences/reorder`, { ordered_ids: orderedIds }, { headers: getAuthHeader() });
  return data;
};

// ─── Admin Bookings ───

export const fetchAdminBookings = async () => {
  const { data } = await axios.get(`${API}/admin/bookings`, { headers: getAuthHeader() });
  return data;
};

export const fetchBookingHistory = async () => {
  const { data } = await axios.get(`${API}/admin/bookings/history`, { headers: getAuthHeader() });
  return data;
};

export const archiveBooking = async (bookingId) => {
  const { data } = await axios.delete(`${API}/admin/bookings/${bookingId}`, { headers: getAuthHeader() });
  return data;
};

export const updateBooking = async (bookingId, updateData) => {
  const { data } = await axios.put(`${API}/admin/bookings/${bookingId}`, updateData, { headers: getAuthHeader() });
  return data;
};

export const restoreBooking = async (bookingId) => {
  const { data } = await axios.post(`${API}/admin/bookings/restore/${bookingId}`, {}, { headers: getAuthHeader() });
  return data;
};

export const createManualBooking = async (bookingData) => {
  const { data } = await axios.post(`${API}/admin/bookings`, bookingData, { headers: getAuthHeader() });
  return data;
};

export const deleteHistoryItems = async (bookingIds) => {
  const { data } = await axios.post(`${API}/admin/bookings/history/delete`, { booking_ids: bookingIds }, { headers: getAuthHeader() });
  return data;
};

export const exportHistoryCSV = async () => {
  const response = await axios.get(`${API}/admin/bookings/history/export`, {
    headers: getAuthHeader(),
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = "booking_history.csv";
  a.click();
  window.URL.revokeObjectURL(url);
};

export const archiveAllBookings = async () => {
  const { data } = await axios.delete(`${API}/admin/bookings`, { headers: getAuthHeader() });
  return data;
};

// ─── Admin Discount Codes ───

export const fetchDiscountCodes = async () => {
  const { data } = await axios.get(`${API}/admin/discount-codes`, { headers: getAuthHeader() });
  return data;
};

export const createDiscountCode = async (codeData) => {
  const { data } = await axios.post(`${API}/admin/discount-codes`, codeData, { headers: getAuthHeader() });
  return data;
};

export const updateDiscountCode = async (codeId, codeData) => {
  const { data } = await axios.put(`${API}/admin/discount-codes/${codeId}`, codeData, { headers: getAuthHeader() });
  return data;
};

export const deleteDiscountCode = async (codeId) => {
  const { data } = await axios.delete(`${API}/admin/discount-codes/${codeId}`, { headers: getAuthHeader() });
  return data;
};

// ─── Public Discount Validation ───

export const validateDiscountCode = async (code, experienceId, category, qty) => {
  const { data } = await axios.post(`${API}/validate-discount`, { code, experience_id: experienceId, category, qty });
  return data;
};

export const exportBookingsCSV = async () => {
  const response = await axios.get(`${API}/admin/bookings/export`, {
    headers: getAuthHeader(),
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bookings_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Seed ───
export const seedData = async () => {
  const { data } = await axios.post(`${API}/admin/seed`);
  return data;
};

// ─── File Upload ───
export const uploadImage = async (file, onProgress) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await axios.post(`${API}/admin/upload`, formData, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (e) => {
      if (onProgress && e.total) {
        onProgress(Math.round((e.loaded * 100) / e.total));
      }
    },
  });
  // Return relative URL — frontend resolves it when displaying
  return data.url;
};

// ─── Image URL resolver ───
export const resolveImageUrl = (url) => {
  if (!url) return "";
  // Convert Google Drive sharing links to direct image URLs
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch) return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
  const driveOpen = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
  if (driveOpen) return `https://drive.google.com/uc?export=view&id=${driveOpen[1]}`;
  // Already a full URL (external images)
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // Relative path from our uploads — prepend backend URL
  return `${BACKEND_URL}${url}`;
};
